import { isAcceptedCurrency, type ReceivableInvoiceLineItemRevenue, useApiDealsV1RevenueList } from 'api';
import { logger } from 'monitor';
import { useParams } from 'react-router-dom';
import {
  BorderedLinearProgress,
  Chip,
  CircularProgress,
  DataGrid,
  formatCurrency,
  formatNumber,
  type GridColDef,
  Link,
  Page,
  Typography,
} from 'ui';
import { ArrowCircleLeft } from 'ui/icons';

import { getColorForInvoiceStatus } from '~/dealhub/components/getColorForInvoiceStatus';
import { InvoiceStatus } from '~/invoice/enums';

const columns: GridColDef<ReceivableInvoiceLineItemRevenue>[] = [
  {
    field: 'description',
    headerName: 'Description',
    width: 550,
  },
  { field: 'id', headerName: 'ID' },
  { field: 'quantity', headerName: 'Quantity' },
  { field: 'unit_amount', headerName: 'Unit Amount' },
  {
    field: 'line_total',
    headerName: 'Line Total',
    valueGetter: (params) => {
      if (params.row.quantity && params.row.unit_amount) {
        return Number(params.row.quantity) * Number(params.row.unit_amount);
      }
    },
  },
  {
    field: 'invoice_status',
    headerName: 'Invoice Status',
    renderCell: (params) => {
      const statusColor = getColorForInvoiceStatus(params.value);
      return <Chip label={params.value} color={statusColor} />;
    },
    width: 150,
  },
  { field: 'invoice_xero_id', headerName: 'Invoice Xero ID' },
  { field: 'invoice_xero_amount_paid', headerName: 'Invoice Xero Amount Paid' },
  {
    field: 'invoice_xero_fully_paid_on',
    headerName: 'Invoice Xero Fully Paid On',
  },
];

const getSumOfLineTotal = (data: ReceivableInvoiceLineItemRevenue[]) => {
  // sum of each Invoice Line Item total
  return data.reduce((acc, item) => acc + (Number(item.quantity) || 0) * (Number(item.unit_amount) || 0), 0);
};

const getSumOfAmountPaid = (data: ReceivableInvoiceLineItemRevenue[]) => {
  // filter line items by PAID status and sum the amount paid
  return data
    .filter((item) => item.invoice_status === InvoiceStatus.PAID)
    .reduce((acc, item) => acc + (Number(item.quantity) || 0) * (Number(item.unit_amount) || 0), 0);
};

const getPercentagePaid = (linesTotal: number, amountPaid: number) => {
  return (amountPaid / linesTotal) * 100;
};

export const DealRevenueDetails = () => {
  const { deal_id } = useParams();

  const { data, isFetching } = useApiDealsV1RevenueList(Number(deal_id), {
    query: {
      enabled: Boolean(deal_id),
    },
  });

  if (isFetching) {
    return <CircularProgress />;
  }

  if (!data || data.length === 0) {
    logger.error('No data found for deal', { deal_id });
    return null;
  }

  const totalAmountPaid = getSumOfAmountPaid(data);
  const linesTotal = getSumOfLineTotal(data);
  const percentagePaid = getPercentagePaid(linesTotal, totalAmountPaid);

  // all line items have the same currency code
  const currencyCode = data.length ? data[0].invoice_currency_code : '';

  const currency = isAcceptedCurrency(currencyCode) ? currencyCode : '';

  return (
    <Page>
      <Link href={`/post-deal/dealhub/deal/details/${deal_id}/`}>
        <ArrowCircleLeft />
        Back to deal # {deal_id}
      </Link>
      <Typography variant="h4">
        {formatCurrency(totalAmountPaid, {
          currencyCode: currency,
        })}
        &nbsp;of&nbsp;
        {formatCurrency(linesTotal, {
          currencyCode: currency,
        })}
        &nbsp;paid (
        {formatNumber(percentagePaid, {
          minimumFractionDigits: 2,
          style: 'percent',
        })}
        )
      </Typography>
      <BorderedLinearProgress variant="determinate" value={percentagePaid} />
      <DataGrid rows={data} columns={columns} hideFooter sx={{ overflowX: 'scroll' }} />
    </Page>
  );
};
